import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tokens } from '../styles/tokens';
import { shared } from '../styles/shared';
import { validateField } from '../services/dialog-engine';
import { STRINGS } from '../data/strings';
import type {
  DialogNode,
  FormField,
  FormContent,
  Departement,
} from '../types';

/**
 * Formulaire dynamique généré depuis un nœud de dialogue `type: 'form'`.
 *
 * Émissions :
 *  - `valid-change` : à chaque saisie ou changement, avec `{ valid, data }`
 *    où `data = { key: <cle-du-form>, value: Record<champ, valeur> }`.
 *    Pour le champ `formDept` (select département), la valeur émise est
 *    l'objet `Departement` complet (et non l'id seul) — comportement
 *    aligné sur app.js d'origine.
 *  - `open-login` : clic sur "Connectez-vous" affiché quand l'email est
 *    déjà connu (prop `exists=true` et champ courant `email`).
 *
 * Validation : déléguée à `validateField()` de `services/dialog-engine.ts`
 * — pas de logique dupliquée ici.
 *
 * Simplification vs Angular original : remplacement de `<mat-form-field>` +
 * `<mat-select>` + `<mat-datepicker>` par des `<label>` / `<input>` /
 * `<select>` / `<textarea>` natifs. Le visuel reproduit le rendu du bundle
 * via les styles partagés (cf. shared.ts) — pas d'écart fonctionnel pour
 * l'utilisateur.
 *
 * Cas particulier `formDept` : le `<select>` natif à 95 options est remplacé
 * par un combobox de recherche (input + listbox filtrée). Pour tout autre
 * champ de type `select`, on garde le `<select>` natif.
 */
@customElement('sarah-formulaire')
export class SarahFormulaire extends LitElement {
  static styles = [
    tokens,
    shared,
    css`
      :host { display: block; }

      .form-container {
        margin-top: 50px;
        margin-bottom: 50px;
        padding: 0 10px;
      }
      @media (min-width: 720px) {
        .form-container { margin-left: 62px; }
      }

      .email-exists {
        background: var(--sarah-bleu);
        color: #fff;
        padding: 10px 15px;
        border-radius: 6px;
        margin-top: 12px;
        animation: simpleFade .6s ease;
      }
      .email-exists span { font-weight: 600; }
      .email-exists button.log {
        background: transparent;
        color: #fff;
        border: none;
        text-decoration: underline;
        font-size: inherit;
        padding: 0;
        margin-left: 4px;
        cursor: pointer;
        font-family: inherit;
      }
      @keyframes simpleFade {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .combobox-wrap {
        position: relative;
      }
      .combobox-list {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 10;
        margin: 4px 0 0;
        padding: 4px 0;
        list-style: none;
        background: #fff;
        border: 1px solid var(--sarah-border);
        border-radius: 6px;
        box-shadow: var(--sarah-card-shadow);
        max-height: 280px;
        overflow-y: auto;
      }
      .combobox-list li {
        padding: 10px 16px;
        font-size: 16px;
        color: var(--sarah-text);
        cursor: pointer;
        line-height: 1.3;
      }
      .combobox-list li:hover { background: #f5f3ed; }
      .combobox-list li[aria-selected="true"],
      .combobox-list li.active { background: #eef3fa; }
      .combobox-empty {
        padding: 10px 16px;
        font-size: 14px;
        color: #888;
        font-style: italic;
      }
    `,
  ];

  @property({ type: Object }) formulaire: DialogNode | null = null;

  @property({ type: Object, attribute: false }) savedForm: FormContent | null = null;

  @property({ type: Boolean }) exists = false;

  @property({ type: Array }) departements: Departement[] = [];

  @state() private values: Record<string, unknown> = {};

  @state() private errors: Record<string, string | null> = {};

  @state() private touched: Record<string, boolean> = {};

  @state() private deptQuery = '';

  @state() private deptOpen = false;

  @state() private deptActiveIndex = -1;

  private get fields(): FormField[] {
    if (!this.formulaire) return [];
    return this.formulaire.message as FormField[];
  }

  private get minDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  willUpdate(changed: Map<string, unknown>): void {
    // Ne réinitialise QUE quand le nœud form change. Si on écoutait aussi
    // `savedForm`, on entrerait en boucle infinie : l'init émet `valid-change`
    // → le parent met à jour son `currentFormContent` → repasse une nouvelle
    // ref de `savedForm` à chaque render → re-init → re-emit → boucle.
    if (changed.has('formulaire')) {
      this.initState();
    }
  }

  private initState(): void {
    const values: Record<string, unknown> = {};
    const errors: Record<string, string | null> = {};
    const touched: Record<string, boolean> = {};

    const saved =
      this.savedForm && typeof this.savedForm === 'object'
        ? this.savedForm.value || {}
        : {};

    for (const field of this.fields) {
      const raw = saved[field.key];
      values[field.key] = raw !== undefined && raw !== null ? raw : '';
      errors[field.key] = null;
      touched[field.key] = raw !== undefined && raw !== null && raw !== '';
    }

    this.values = values;
    this.errors = errors;
    this.touched = touched;

    // Re-synchronise l'input du combobox avec une valeur potentiellement
    // restaurée depuis savedForm (l'utilisateur revient en arrière).
    const dept = values['formDept'];
    this.deptQuery = this.isDepartement(dept) ? dept.full_departement : '';
    this.deptOpen = false;
    this.deptActiveIndex = -1;

    queueMicrotask(() => this.emitValidity());
  }

  private isDepartement(v: unknown): v is Departement {
    return !!v && typeof v === 'object' && 'id_departement' in (v as object);
  }

  private hasRequired(field: FormField): boolean {
    return (field.validations || []).some((v) => v.name === 'required');
  }

  private rawValueForValidation(field: FormField, value: unknown): unknown {
    // Le select département stocke l'objet complet ; pour la validation
    // `required`, on s'appuie sur l'id (chaîne vide = pas choisi).
    if (field.type === 'select' && value && typeof value === 'object') {
      const dept = value as Departement;
      return dept.id_departement != null ? String(dept.id_departement) : '';
    }
    return value;
  }

  private computeErrors(): Record<string, string | null> {
    const errors: Record<string, string | null> = {};
    for (const field of this.fields) {
      const raw = this.rawValueForValidation(field, this.values[field.key]);
      errors[field.key] = validateField(field, raw);
    }
    return errors;
  }

  private emitValidity(): void {
    if (!this.formulaire) return;
    const errors = this.computeErrors();
    this.errors = errors;
    const valid = Object.values(errors).every((e) => e === null);
    const data: FormContent = {
      key: this.formulaire.cle,
      value: { ...this.values },
    };
    this.dispatchEvent(
      new CustomEvent('valid-change', {
        detail: { valid, data },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onFieldInput(field: FormField, e: Event): void {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const raw = target.value;

    let stored: unknown = raw;
    if (field.type === 'select' && field.key === 'formDept') {
      const dept = this.departements.find(
        (d) => String(d.id_departement) === String(raw),
      );
      stored = dept ? dept : raw;
    }

    this.values = { ...this.values, [field.key]: stored };
    if (raw !== '') {
      this.touched = { ...this.touched, [field.key]: true };
    }
    this.emitValidity();
  }

  private onFieldBlur(field: FormField): void {
    this.touched = { ...this.touched, [field.key]: true };
    this.errors = this.computeErrors();
  }

  private onOpenLogin(): void {
    const email = String(this.values['formEmail'] || '');
    this.dispatchEvent(
      new CustomEvent('open-login', {
        detail: { email },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // ====================================================================
  // Combobox département
  // ====================================================================

  private fold(s: string): string {
    // Pliage casse + suppression des diacritiques pour la recherche.
    // ̀-ͯ = "Combining Diacritical Marks" produits par NFD.
    return s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  private get filteredDepartements(): Departement[] {
    const q = this.fold(this.deptQuery.trim());
    if (!q) return this.departements;
    // Si le query matche exactement un département déjà sélectionné (input
    // contient le `full_departement`), on affiche toute la liste — l'user
    // veut probablement changer son choix, pas filtrer sur sa propre valeur.
    const exact = this.departements.find(
      (d) => this.fold(d.full_departement) === q,
    );
    if (exact) return this.departements;
    return this.departements.filter((d) => {
      const nom = this.fold(d.nom_departement || '');
      const num = this.fold(d.num_departement || '');
      return nom.includes(q) || num.startsWith(q);
    });
  }

  private commitDepartement(field: FormField, dept: Departement): void {
    this.values = { ...this.values, [field.key]: dept };
    this.touched = { ...this.touched, [field.key]: true };
    this.deptQuery = dept.full_departement;
    this.deptOpen = false;
    this.deptActiveIndex = -1;
    this.emitValidity();
  }

  private onDeptInput(field: FormField, e: Event): void {
    const target = e.target as HTMLInputElement;
    this.deptQuery = target.value;
    this.deptOpen = true;
    this.deptActiveIndex = -1;

    // Tant que l'utilisateur n'a pas choisi une option, on n'a pas de
    // département validé : on stocke chaîne vide pour faire échouer la
    // validation `required` (qui regarde id_departement).
    const exact = this.departements.find(
      (d) => this.fold(d.full_departement) === this.fold(this.deptQuery),
    );
    if (exact) {
      this.values = { ...this.values, [field.key]: exact };
    } else {
      this.values = { ...this.values, [field.key]: '' };
    }
    this.emitValidity();
  }

  private onDeptFocus(): void {
    this.deptOpen = true;
  }

  private onDeptBlur(field: FormField): void {
    // Léger délai : laisser le temps au `mousedown` sur une option de tirer
    // avant qu'on ferme la liste (sinon le clic n'a pas le temps de
    // déclencher `commitDepartement`).
    window.setTimeout(() => {
      this.deptOpen = false;
      this.touched = { ...this.touched, [field.key]: true };
      this.errors = this.computeErrors();
    }, 120);
  }

  private onDeptKeydown(field: FormField, e: KeyboardEvent): void {
    const list = this.filteredDepartements;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!this.deptOpen) {
        this.deptOpen = true;
        this.deptActiveIndex = 0;
        return;
      }
      this.deptActiveIndex = Math.min(this.deptActiveIndex + 1, list.length - 1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!this.deptOpen) {
        this.deptOpen = true;
        this.deptActiveIndex = list.length - 1;
        return;
      }
      this.deptActiveIndex = Math.max(this.deptActiveIndex - 1, 0);
      return;
    }
    if (e.key === 'Enter') {
      if (this.deptOpen && this.deptActiveIndex >= 0 && list[this.deptActiveIndex]) {
        e.preventDefault();
        this.commitDepartement(field, list[this.deptActiveIndex]);
      }
      return;
    }
    if (e.key === 'Escape') {
      if (this.deptOpen) {
        e.preventDefault();
        this.deptOpen = false;
        this.deptActiveIndex = -1;
      }
      return;
    }
  }

  private renderDeptCombobox(field: FormField) {
    const listId = `dept-list-${field.key}`;
    const list = this.filteredDepartements;
    return html`
      <div class="combobox-wrap">
        <input
          type="text"
          name=${field.key}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded=${this.deptOpen ? 'true' : 'false'}
          aria-controls=${listId}
          autocomplete="off"
          .value=${this.deptQuery}
          @input=${(e: Event) => this.onDeptInput(field, e)}
          @focus=${this.onDeptFocus}
          @blur=${() => this.onDeptBlur(field)}
          @keydown=${(e: KeyboardEvent) => this.onDeptKeydown(field, e)}
        />
        ${this.deptOpen
          ? html`
              <ul id=${listId} class="combobox-list" role="listbox">
                ${list.length === 0
                  ? html`<li class="combobox-empty" role="presentation">
                      Aucun département trouvé
                    </li>`
                  : list.map(
                      (d, i) => html`
                        <li
                          role="option"
                          class=${i === this.deptActiveIndex ? 'active' : ''}
                          aria-selected=${i === this.deptActiveIndex ? 'true' : 'false'}
                          @mousedown=${(e: Event) => {
                            // mousedown plutôt que click : se déclenche avant blur,
                            // sinon le setTimeout du blur ferme la liste avant le clic.
                            e.preventDefault();
                            this.commitDepartement(field, d);
                          }}
                        >
                          ${d.full_departement}
                        </li>
                      `,
                    )}
              </ul>
            `
          : nothing}
      </div>
    `;
  }

  private renderField(field: FormField) {
    const label = field.label + (this.hasRequired(field) ? ' *' : '');
    const err = this.errors[field.key];
    const showErr = err && this.touched[field.key];
    const v = this.values[field.key];

    const input = (() => {
      switch (field.type) {
        case 'select': {
          if (field.key === 'formDept') {
            return this.renderDeptCombobox(field);
          }
          const selectedId =
            v && typeof v === 'object'
              ? String((v as Departement).id_departement || '')
              : String(v || '');
          return html`
            <select
              name=${field.key}
              .value=${selectedId}
              @change=${(e: Event) => this.onFieldInput(field, e)}
              @blur=${() => this.onFieldBlur(field)}
            >
              <option value="">-</option>
              ${this.departements.map(
                (d) => html`
                  <option value=${String(d.id_departement)}>
                    ${d.full_departement}
                  </option>
                `,
              )}
            </select>
          `;
        }
        case 'textarea':
          return html`
            <textarea
              name=${field.key}
              .value=${String(v || '')}
              @input=${(e: Event) => this.onFieldInput(field, e)}
              @blur=${() => this.onFieldBlur(field)}
            ></textarea>
          `;
        case 'date':
          return html`
            <input
              type="date"
              name=${field.key}
              min=${this.minDate}
              .value=${String(v || '')}
              autocomplete="off"
              @input=${(e: Event) => this.onFieldInput(field, e)}
              @change=${(e: Event) => this.onFieldInput(field, e)}
              @blur=${() => this.onFieldBlur(field)}
            />
          `;
        default:
          return html`
            <input
              type=${field.type}
              name=${field.key}
              .value=${String(v || '')}
              autocomplete="off"
              @input=${(e: Event) => this.onFieldInput(field, e)}
              @blur=${() => this.onFieldBlur(field)}
            />
          `;
      }
    })();

    return html`
      <div class="field">
        <label>${label}</label>
        ${input}
        ${showErr
          ? html`<div class="field-error">${err}</div>`
          : nothing}
        ${this.exists && field.type === 'email'
          ? html`
              <div class="email-exists">
                <span>
                  ${STRINGS.emailExists}<br />
                  <button type="button" class="log" @click=${this.onOpenLogin}>
                    Connectez-vous
                  </button>
                </span>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  render() {
    if (!this.formulaire) return nothing;
    return html`
      <div class="form-container">
        <form novalidate autocomplete="off" @submit=${(e: Event) => e.preventDefault()}>
          ${this.fields.map((f) => this.renderField(f))}
        </form>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sarah-formulaire': SarahFormulaire;
  }
}
